import React, { useState, useEffect } from 'react'
import QRCodeStand from '../../../Assets/QRCodeStand.png'
import TagNFC from "../../../Assets/TagNFC.jpeg"
import PurchaseModal from './PurchaseModal'
import { baseUrl } from '../../../Environments/environment'
import { apiClient } from '../../../api'

const QRCodeKitSkeleton = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Skeleton */}
        <div className="sm:flex sm:items-center justify-between mt-6">
          <div className="sm:flex-auto">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Items Grid Skeleton */}
        <div className="mt-8 space-y-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image Section Skeleton */}
                <div className="w-full sm:w-64 h-64 sm:h-auto flex-shrink-0 bg-gray-200"></div>

                {/* Content Section Skeleton */}
                <div className="flex-1 p-6 flex flex-col justify-between relative">
                  {/* Checkbox and Price Skeleton */}
                  <div className="absolute top-6 right-6 flex items-center gap-3">
                    <div className="text-right space-y-1">
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                      <div className="h-3 w-20 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-5 w-5 bg-gray-200 rounded"></div>
                  </div>

                  <div className="pr-32 space-y-3">
                    {/* Heading Skeleton */}
                    <div className="h-6 w-40 bg-gray-200 rounded"></div>

                    {/* Detail Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      <div className="space-y-1">
                        <div className="h-3 w-full bg-gray-200 rounded"></div>
                        <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
                        <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const QRCodeKit = () => {
  // Sample data - replace with actual data from API or props
  const items = [
    {
      id: 1,
      name: 'QR Code Scanner',
      detail: {
        heading: 'This item includess',
        points: [
          'PVC Stand',
          'NFC attached to PVC Stand',
          'QR code and design printed on PVC stand'
        ]
      },
      price: 30,
      image: QRCodeStand
    },
    {
      id: 2,
      name: 'NFC Tags',
      detail: {
        heading: 'Elegant waterproof NFC table tags for effortless restaurant reviews.',
        points: []
      },
      price: 15,
      image: TagNFC
    }
  ]

  const [selectedItems, setSelectedItems] = useState(new Set())
  const [quantities, setQuantities] = useState({})
  const [quantityInputs, setQuantityInputs] = useState({})
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)
  const [orderDetails, setOrderDetails] = useState(null)
  const [orderedItemIds, setOrderedItemIds] = useState(new Set())
  const [loading, setLoading] = useState(true)

  const handleCheckboxChange = (itemId) => {
    // Prevent selection if item is already purchased
    if (orderedItemIds.has(itemId)) {
      return
    }
    
    setSelectedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
        // Initialize quantity to 1 if not set
        if (!quantities[itemId]) {
          setQuantities((prevQty) => ({ ...prevQty, [itemId]: 1 }))
          setQuantityInputs((prev) => ({ ...prev, [itemId]: '1' }))
        }
      }
      return newSet
    })
  }

  const isItemPurchased = (itemId) => {
    return orderedItemIds.has(itemId)
  }

  const handleQuantityChange = (itemId, value) => {
    // Allow empty string for clearing
    if (value === '') {
      setQuantityInputs((prev) => ({ ...prev, [itemId]: '' }))
      setQuantities((prev) => ({ ...prev, [itemId]: 1 })) // Keep calculation at 1
      return
    }

    const numValue = parseInt(value)
    if (isNaN(numValue) || numValue < 1) {
      return
    }

    setQuantityInputs((prev) => ({ ...prev, [itemId]: value }))
    setQuantities((prev) => ({ ...prev, [itemId]: numValue }))
  }

  const fetchOrderDetails = async () => {
    setLoading(true)
    try {
      const propertyId = localStorage.getItem("selectedPropertyId")
      const token = localStorage.getItem("accessToken")
      const response = await apiClient.get(
        `${baseUrl}/qrKitOrder/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      if (response.status === 200) {
        const orders = response.data.result?.qrKitOrders || []
        setOrderDetails(orders)
        
        // Extract itemIds only from orders with status "completed"
        const orderedIds = new Set()
        orders.forEach((order) => {
          // Only include items from completed orders
          if ((order.orderStatus === 'placed' || order.orderStatus === 'dispatched' || order.orderStatus === 'processing') && order.orderItems && Array.isArray(order.orderItems)) {
            order.orderItems.forEach((orderItem) => {
              if (orderItem.itemId) {
                orderedIds.add(orderItem.itemId)
              }
            })
          }
        })
        
        setOrderedItemIds(orderedIds)
        console.log("Order details fetched successfully", orders)
        console.log("Ordered item IDs (completed orders only):", Array.from(orderedIds))
      }
    } catch (error) {
      console.error("Error fetching order details", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrderDetails()
  }, [])

  const handleQuantityBlur = (itemId) => {
    const inputValue = quantityInputs[itemId]
    if (inputValue === '' || inputValue === undefined || parseInt(inputValue) < 1) {
      setQuantityInputs((prev) => ({ ...prev, [itemId]: '1' }))
      setQuantities((prev) => ({ ...prev, [itemId]: 1 }))
    }
  }

  const getQuantity = (itemId) => {
    return quantities[itemId] || 1
  }

  const getQuantityInputValue = (itemId) => {
    return quantityInputs[itemId] !== undefined ? quantityInputs[itemId] : (quantities[itemId] || '1')
  }

  const calculateTotal = () => {
    return Array.from(selectedItems).reduce((total, itemId) => {
      const item = items.find((i) => i.id === itemId)
      const quantity = getQuantity(itemId)
      return total + (item ? item.price * quantity : 0)
    }, 0)
  }

  const totalPrice = calculateTotal()

  if (loading) {
    return <QRCodeKitSkeleton />
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="sm:flex sm:items-center justify-between mt-6 animate-fadeIn">
          <div className="sm:flex-auto">
            <h1 className="text-2xl text-gray-800 tracking-tight">QR Code Kit</h1>
            <p className="mt-2 text-sm text-gray-600 leading-6">
              Purchase items for your property's reviews.
            </p>
          </div>
          {selectedItems.size > 0 && (
            <div className="flex items-center gap-4 mt-4 sm:mt-0">
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-semibold text-blue-600">
                  ${totalPrice.toFixed(2)}
                </p>
              </div>
              <button
                onClick={() => setIsPurchaseModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                Purchase
              </button>
            </div>
          )}
        </div>

        {/* Items Grid */}
        <div className="mt-8 space-y-6">
          {items.map((item) => {
            const isPurchased = isItemPurchased(item.id)
            return (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image Section - Left */}
                <div className="w-full sm:w-64 h-64 sm:h-auto flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content Section - Right */}
                <div className="flex-1 p-6 flex flex-col justify-between relative">
                  {/* Checkbox, Price, and Quantity on the right */}
                  <div className="absolute top-6 right-6 flex flex-col items-end gap-3">
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className="text-lg font-medium text-blue-600 block">
                          ${item.price.toFixed(2)}
                        </span>
                        {item.id === 2 && selectedItems.has(item.id) && (
                          <span className="text-xs text-gray-500">
                            × {getQuantity(item.id)} = ${(item.price * getQuantity(item.id)).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                        disabled={isPurchased}
                        className={`w-5 h-5 text-blue-600 border-2 rounded focus:ring-blue-500 bg-white ${
                          isPurchased
                            ? 'border-gray-300 cursor-not-allowed opacity-50'
                            : 'border-blue-400 cursor-pointer checked:bg-blue-600 checked:border-blue-600'
                        }`}
                      />
                    </div>
                    {/* Quantity input for NFC Tags below checkbox */}
                    {item.id === 2 && selectedItems.has(item.id) && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Qty:</label>
                        <input
                          type="number"
                          min="1"
                          value={getQuantityInputValue(item.id)}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          onBlur={() => handleQuantityBlur(item.id)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>

                  <div className="pr-32">
                    {/* 1. Heading (Name of the item) */}
                    <div className="flex items-center gap-2 mb-3">
                      <h2 className="text-xl font-medium text-gray-900">
                        {item.name}
                      </h2>
                      {isPurchased && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">
                          Purchased
                        </span>
                      )}
                    </div>

                    {/* 2. Detail of the item */}
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-900 mb-2">
                        {item.detail.heading}
                      </h3>
                      <ul className="list-disc list-inside space-y-1">
                        {item.detail.points.map((point, index) => (
                          <li key={index} className="text-sm text-gray-600">
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )
          })}
        </div>

        {/* Purchase Modal */}
        <PurchaseModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          selectedItems={selectedItems}
          quantities={quantities}
          items={items}
          totalPrice={totalPrice}
          onOrderSuccess={async () => {
            // Clear selected items and quantities after successful order
            setSelectedItems(new Set())
            setQuantities({})
            setQuantityInputs({})
            // Wait a bit for the database to update, then refresh order details
            setTimeout(() => {
              fetchOrderDetails()
            }, 2000)
          }}
        />
      </div>
    </div>
  )
}

export default QRCodeKit
